// The page's words, in the page's own language (spec 2026-10-10 §Ship 3, package table).
// Language comes from <html lang> and nothing else — never a cookie, never the browser's guess.
import { pageLanguage } from './locales.js';

const walk = (table, key) => key.split('.').reduce((node, part) => (node && typeof node === 'object') ? node[part] : undefined, table);

// Every tool's strings.js calls this once at import with its five tables; its main.js then reads
// S.<key> exactly as before. The five tables are held to the same key set, kinds and placeholders
// by a per-tool unit test, so a missing key cannot reach a page; a missing whole table is reported
// once and served in English rather than crashing a half-loaded tool.
export function pickTable(tables) {
  const id = pageLanguage().id;
  const table = tables[id] ?? tables.en;
  if (!tables[id]) console.error(`i18n: no ${id} table beside the English one — serving English`);
  return table;
}

// Deep-merge a tool's own table under the package's shared one (ship 3's dedupe): objects merge
// key by key, and anything else in the tool's table wins over the shared half. Pure — a page, a
// worker and the build may all call it; nothing here reads the page.
export function compose(shared, own) {
  const out = { ...shared };
  for (const [k, v] of Object.entries(own || {})) {
    const isPlain = (x) => x !== null && typeof x === 'object' && !Array.isArray(x);
    out[k] = isPlain(v) && isPlain(out[k]) ? compose(out[k], v) : v;
  }
  return out;
}

// t(key, …params) over the shared words: a leaf is text, or a function that takes the params
// (number grouping and the like stay inside each language's own file). A key the page's table
// lacks falls back to English; a key no table knows reports once per key and shows the key itself,
// so a missing word is visible on the page that missed it, not silently blank.
export function createT(tables) {
  const id = pageLanguage().id;
  const table = tables[id] ?? tables.en;
  const reported = new Set();
  return (key, ...params) => {
    const own = walk(table, key);
    const leaf = own !== undefined ? own : walk(tables.en, key);
    if (leaf === undefined) {
      if (!reported.has(key)) { reported.add(key); console.error(`i18n: no word for "${key}" in ${id} or English`); }
      return key;
    }
    return typeof leaf === 'function' ? leaf(...params) : leaf;
  };
}
